import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { MediaBlockService } from './media-block-service';
import { Coordinate } from '@kinomap-dev/ng-ui-lib/core';

export type SyncModeType = 'auto' | 'manual' | 'none';


@Injectable({
  providedIn: 'root',
})
export class AutoSyncService {

  public syncMode: WritableSignal<SyncModeType> = signal('none');
  public mediaBlockService: MediaBlockService = inject(MediaBlockService);

  public setAutoMode(): void {
    this.syncMode.set('auto');
  }

  public setManualMode(): void {
    this.syncMode.set('manual');
  }

  /**
   * Fonction qui dois controller si il est possible de faire une 
   * AutoSycnro entre le GPS et la video
   * @returns boolean
   */
  async controlIfMediaCanBeSynchronised(): Promise<boolean> {
    const videoRecordedDate: Date | null = await this.getVideoRecordDate();
    if (!videoRecordedDate) {
      this.syncMode.set('none');
      return false;
    }
    const canAutoSync: boolean = this.dateIncludedInGpsCoordinates(videoRecordedDate);
    this.syncMode.set(canAutoSync ? 'auto' : 'none');
    console.log('[AutoSync] findClosestCoordinate :', this.findClosestCoordinate(videoRecordedDate));
    return canAutoSync;
  }

  /**
   * Extrait la date d'enregistrement réelle de la vidéo depuis les métadonnées
   * du conteneur MP4 (boîte mvhd), puis se replie sur lastModified si absent.
   * @returns la date d'enregistrement ou null
   */
  private async getVideoRecordDate(): Promise<Date | null> {
    const videoFile: File | undefined = this.mediaBlockService.videoFile();
    if (!videoFile) {
      console.log('[AutoSync] getVideoRecordDate: aucun fichier vidéo trouvé');
      return null;
    }

    console.log('[AutoSync] getVideoRecordDate: fichier vidéo détecté', {
      name: videoFile.name,
      size: videoFile.size,
      lastModified: new Date(videoFile.lastModified),
    });

    try {
      // Lire les 512 premiers Ko (moov au début)
      const chunkSize = 512 * 1024;
      const startBuffer = await videoFile.slice(0, chunkSize).arrayBuffer();
      let metaDate = this.extractMp4CreationDate(startBuffer);

      if (!metaDate && videoFile.size > chunkSize) {
        // Sur iPhone/Android, moov est à la FIN du fichier.
        // On lit les 8 derniers octets pour récupérer la taille + type de la dernière box,
        // puis on lit exactement cette box si c'est bien moov.
        console.log('[AutoSync] getVideoRecordDate: moov absent au début, lecture de la dernière box…');
        const tailView = new DataView(await videoFile.slice(-8).arrayBuffer());
        const lastBoxSize = tailView.getUint32(0);
        const lastBoxType = String.fromCharCode(
          tailView.getUint8(4), tailView.getUint8(5),
          tailView.getUint8(6), tailView.getUint8(7),
        );
        console.log('[AutoSync] getVideoRecordDate: dernière box →', lastBoxType, 'taille =', lastBoxSize);

        if (lastBoxType === 'moov' && lastBoxSize > 8 && lastBoxSize <= videoFile.size) {
          const moovBuffer = await videoFile.slice(-lastBoxSize).arrayBuffer();
          metaDate = this.extractMp4CreationDate(moovBuffer);
        }
      }

      if (metaDate) {
        console.log('[AutoSync] getVideoRecordDate: date extraite des métadonnées MP4 →', metaDate);
        return metaDate;
      } else {
        const fallback = new Date(videoFile.lastModified);
        console.warn('[AutoSync] getVideoRecordDate: métadonnée MP4 absente, fallback sur lastModified →', fallback);
        return fallback;
      }
    } catch (err) {
      const fallback = new Date(videoFile.lastModified);
      console.error('[AutoSync] getVideoRecordDate: erreur lecture buffer, fallback sur lastModified →', fallback, err);
      return fallback;
    }
  }

  /**
   * Parcourt les boîtes MP4 de haut niveau pour trouver la boîte moov,
   * puis délègue l'extraction de la date à findMvhdDate.
   */
  private extractMp4CreationDate(buffer: ArrayBuffer): Date | null {
    const view = new DataView(buffer);
    let offset = 0;

    while (offset < view.byteLength - 8) {
      const boxSize = view.getUint32(offset);
      const boxType = String.fromCharCode(
        view.getUint8(offset + 4),
        view.getUint8(offset + 5),
        view.getUint8(offset + 6),
        view.getUint8(offset + 7),
      );

      if (boxType === 'moov') {
        console.log('[AutoSync] extractMp4CreationDate: boîte moov trouvée à offset', offset);
        return this.findMvhdDate(view, offset + 8, offset + boxSize);
      }

      if (boxSize < 8) break;
      offset += boxSize;
    }

    console.warn('[AutoSync] extractMp4CreationDate: boîte moov introuvable dans les 512 Ko lus');
    return null;
  }

  /**
   * Cherche la boîte mvhd dans la boîte moov et lit le champ creation_time.
   * Les timestamps MP4 sont en secondes depuis le 1er janvier 1904.
   */
  private findMvhdDate(view: DataView, start: number, end: number): Date | null {
    let offset = start;

    while (offset < end - 8) {
      const boxSize = view.getUint32(offset);
      const boxType = String.fromCharCode(
        view.getUint8(offset + 4),
        view.getUint8(offset + 5),
        view.getUint8(offset + 6),
        view.getUint8(offset + 7),
      );

      if (boxType === 'mvhd') {
        const version = view.getUint8(offset + 8);
        let creationTimeSec: number;
        console.log('[AutoSync] findMvhdDate: boîte mvhd trouvée, version', version);

        if (version === 1) {
          // Version 1 : timestamp sur 64 bits (offset + 12)
          const high = view.getUint32(offset + 12);
          const low = view.getUint32(offset + 16);
          creationTimeSec = high * 0x100000000 + low;
        } else {
          // Version 0 : timestamp sur 32 bits (offset + 12)
          creationTimeSec = view.getUint32(offset + 12);
        }

        console.log('[AutoSync] findMvhdDate: creationTimeSec brut (epoch 1904) =', creationTimeSec);

        if (creationTimeSec === 0) {
          console.warn('[AutoSync] findMvhdDate: creationTimeSec vaut 0, date ignorée');
          return null;
        }

        // Décalage entre l'epoch MP4 (1904-01-01) et l'epoch Unix (1970-01-01)
        const MP4_EPOCH_OFFSET_MS = 2082844800000;
        const date = new Date(creationTimeSec * 1000 - MP4_EPOCH_OFFSET_MS);
        console.log('[AutoSync] findMvhdDate: date convertie →', date);
        return date;
      }

      if (boxSize < 8) break;
      offset += boxSize;
    }

    return null;
  }

  /**
   * Permet de controller si une date est incule dans les coordinates
   * @param videoRecordDate 
   * @returns 
   */
  private dateIncludedInGpsCoordinates(videoRecordDate: Date): boolean {
    const coordinates: Coordinate[] | undefined = this.mediaBlockService.syncedCoordinates();
    if (!coordinates || coordinates.length === 0) return false;

    const videoTime = videoRecordDate.getTime();
    const firstTime = coordinates[0].coo_time * 1000;
    const lastTime = coordinates[coordinates.length - 1].coo_time * 1000;

    return videoTime >= firstTime && videoTime <= lastTime;
  }

  findClosestCoordinate(date: Date): Coordinate | null {
    const coordinates: Coordinate[] | undefined = this.mediaBlockService.syncedCoordinates();
    if (!coordinates || coordinates.length === 0) return null;
    const targetTime: number = date.getTime();

    console.log('[AutoSync] findClosestCoordinate: targetTime (video) =', new Date(targetTime));
    console.log('[AutoSync] findClosestCoordinate: GPS start =', new Date(coordinates[0].coo_time * 1000));
    console.log('[AutoSync] findClosestCoordinate: GPS end   =', new Date(coordinates[coordinates.length - 1].coo_time * 1000));

    let closestIndex = 0;
    coordinates.forEach((current, index) => {
      const currentDiff: number = Math.abs(current.coo_time * 1000 - targetTime);
      const closestDiff: number = Math.abs(coordinates[closestIndex].coo_time * 1000 - targetTime);
      if (currentDiff < closestDiff) closestIndex = index;
    });
    console.log('[AutoSync] findClosestCoordinate: index =', closestIndex, '/', coordinates.length - 1);
    return coordinates[closestIndex];
  }

}












///           1781520200